/**
 * A tiny hash router — consistent with the rest of web/ ("no route library", see App.tsx)
 * but the reviewer dashboard is three levels deep and every finding needs a shareable deep
 * link to a document page, so plain useState switching (as the valuer screens do) isn't
 * enough here.
 *
 * Routes:
 *   #/                                   -> home (browse by valuer)
 *   #/valuer/<id>                        -> a valuer's reports
 *   #/report/<id>[?doc=report|loi&page=N] -> the report workspace, optionally deep-linked
 *                                            to a document + page
 *   #/admin[/<section>]                  -> the admin surface (Phase 6, is_admin only)
 */
import { useCallback, useEffect, useState } from 'react';

export type AdminSection = 'checks' | 'risks' | 'errors' | 'instructions' | 'config' | 'audit';
const ADMIN_SECTIONS: readonly AdminSection[] = ['checks', 'risks', 'errors', 'instructions', 'config', 'audit'];

export type ReviewerRoute =
  | { name: 'home' }
  | { name: 'valuer'; valuerId: string }
  | { name: 'report'; reportId: string; doc?: 'report' | 'loi'; page?: number }
  | { name: 'admin'; section: AdminSection };

export function parseHash(hash: string): ReviewerRoute {
  const clean = hash.replace(/^#\/?/, '');
  const [pathPart, queryPart] = clean.split('?');
  const segments = (pathPart ?? '').split('/').filter(Boolean);
  const params = new URLSearchParams(queryPart ?? '');

  if (segments[0] === 'valuer' && segments[1]) {
    return { name: 'valuer', valuerId: segments[1] };
  }
  if (segments[0] === 'admin') {
    const section = segments[1] ?? '';
    return { name: 'admin', section: (ADMIN_SECTIONS as readonly string[]).includes(section) ? (section as AdminSection) : 'checks' };
  }
  if (segments[0] === 'report' && segments[1]) {
    const doc = params.get('doc');
    const pageStr = params.get('page');
    const page = pageStr ? Number(pageStr) : undefined;
    return {
      name: 'report',
      reportId: segments[1],
      doc: doc === 'loi' || doc === 'report' ? doc : undefined,
      page: page && Number.isFinite(page) ? page : undefined,
    };
  }
  return { name: 'home' };
}

export function routeToHash(route: ReviewerRoute): string {
  if (route.name === 'home') return '#/';
  if (route.name === 'valuer') return `#/valuer/${route.valuerId}`;
  if (route.name === 'admin') return `#/admin/${route.section}`;
  const params = new URLSearchParams();
  if (route.doc) params.set('doc', route.doc);
  if (route.page) params.set('page', String(route.page));
  const qs = params.toString();
  return `#/report/${route.reportId}${qs ? `?${qs}` : ''}`;
}

export function useHashRoute(): [ReviewerRoute, (route: ReviewerRoute) => void] {
  const [route, setRoute] = useState<ReviewerRoute>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((next: ReviewerRoute) => {
    window.location.hash = routeToHash(next);
  }, []);

  return [route, navigate];
}

/**
 * Update the URL to reflect a document/page jump within the current report without
 * pushing a new history entry (every finding click would otherwise flood back-button
 * history).
 */
export function replacePageInUrl(reportId: string, doc: 'report' | 'loi', page: number): void {
  const hash = routeToHash({ name: 'report', reportId, doc, page });
  window.history.replaceState(null, '', hash);
}
